import React, { useEffect, useState } from "react";
import { Accordion, Button, Collapse, Form } from "react-bootstrap";
import SMSCounter from "../Builder/Components/SMSCounter";
import HelpButton from "../HelpContent/HelpButton";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import {
  BroadcastChannelType,
  BroadcastType,
  removeMessage,
  selectBroadcast,
  selectCurrentBroadcastChannel,
  setBroadcast,
  setCurrentBroadcastChannel,
} from "../store/slices/broadcastSlice";
import { proxyAPI } from "../proxyAPI";
import TokenEditor from "../Builder/Components/TokenEditor";
import { useDispatch, useSelector } from "react-redux";
import { useGetContactFieldsQuery } from "../store/slices/contactsSlice";
import DynamicHeaderInput from "../Builder/nodes/Logic/Inputs/DynamicHeaderInput";

const compulsoryRedirectMessage = "Press 9 to divert this call. ";

const MyAccordionBody = ({ children }: { children: any }) => {
  const values = useSelector(selectBroadcast);
  const dispatch = useDispatch();
  const setValues = (f: (v: BroadcastType) => BroadcastType) =>
    dispatch(setBroadcast(f(values)));

  const currentChannel = useSelector(selectCurrentBroadcastChannel);

  const {
    data: contactFields,
    isFetching,
    isLoading,
  } = useGetContactFieldsQuery();

  return (
    <Accordion.Body>
      <div className="p-3">{children}</div>
      {!values.IsFileImport &&
        // show these options only if there are more than one contact fields
        contactFields?.filter(
          (c: any) =>
            (currentChannel === "sms" && c.isSms) ||
            (currentChannel === "email" && c.isEmail) ||
            (currentChannel === "voice" && c.isVoice)
        ).length > 1 && (
          <>
            <div className="p-3">
              <label className="fw-bold">Send message to fields:</label>
              <Input
                inputType="select"
                options={[
                  { value: "first", label: "First comms channel available" },
                  { value: "onePerField", label: "One message per field" },
                ]}
                value={[
                  values.Messages.find((c: any) => c.Channel === currentChannel)
                    ?.SelectComms,
                ]}
                onChange={([v]: any) =>
                  setValues((p: any) => ({
                    ...p,
                    Messages: p.Messages.map((c: any) => {
                      if (c.Channel === currentChannel) {
                        return {
                          ...c,
                          SelectComms: v,
                        };
                      }
                      return c;
                    }),
                  }))
                }
              />
            </div>
            <div className="p-3 ">
              <label className="fw-bold">Select fields:</label>
              <Input
                inputType="select"
                isMulti
                placeholder="Use any field"
                options={contactFields
                  ?.filter(
                    (c: any) =>
                      (currentChannel === "sms" && c.isSms) ||
                      (currentChannel === "email" && c.isEmail) ||
                      (currentChannel === "voice" && c.isVoice)
                  )
                  .map((c: any) => ({
                    value: c.uuid,
                    label: c.label,
                  }))}
                value={
                  values.Messages.find((c: any) => c.Channel === currentChannel)
                    ?.SelectCommsUuids ?? []
                }
                onChange={(v: any) =>
                  setValues((p: any) => ({
                    ...p,
                    Messages: p.Messages.map((c: any) => {
                      if (c.Channel === currentChannel) {
                        return {
                          ...c,
                          SelectCommsUuids: v,
                        };
                      }
                      return c;
                    }),
                  }))
                }
              />
            </div>
          </>
        )}
      <div className="d-flex justify-content-end my-3">
        <Button
          variant="link"
          className="fw-bold px-3 py-1 btn small border-dark rounded-pill border-2 text-nowrap text-white bg-colored"
          size="sm"
          // disabled={values.Messages.length === 1}
          onClick={() => {
            dispatch(removeMessage(currentChannel));
          }}
        >
          DELETE CHANNEL
        </Button>
      </div>
    </Accordion.Body>
  );
};

export default function BroadcastConfiguration({
  confirmFixedBroadcast,
}: {
  confirmFixedBroadcast: any;
}) {
  const values = useSelector(selectBroadcast);
  const dispatch = useDispatch();
  const setValues = (f: (v: BroadcastType) => BroadcastType) =>
    dispatch(setBroadcast(f(values)));
  const currentChannel = useSelector(selectCurrentBroadcastChannel);

  return (
    <div className="p-3">
      <div className="ms-3 m-2">
        <h5 className="fw-bold">Message Configuration</h5>
        <div className="text-secondary">Configure your broadcast setting.</div>
      </div>
      <Accordion
        // flush
        activeKey={[currentChannel, "contact-fields"]}
        onSelect={(newChannel) => {
          if (newChannel) {
            dispatch(
              setCurrentBroadcastChannel(newChannel as BroadcastChannelType)
            );
          }
        }}
        // style={{ "--bs-accordion-bg": "transparent" }}
      >
        {values.Messages.some((vc) => vc.Channel === "sms") && (
          <Accordion.Item eventKey="sms" className="bg-transparent">
            <Accordion.Header
              style={
                {
                  // "--bs-accordion-active-bg": "white",
                  // "--bs-accordion-btn-bg": "rgba(255,255,255,0.4)",
                }
              }
            >
              <span className="fw-bold">SMS</span>
            </Accordion.Header>
            <MyAccordionBody>
              <div>
                {values.CommsMethod === "two-way" ? (
                  <div className="my-3">
                    <Input
                      inputType="radio"
                      radioName="sms-ticket-type"
                      value={[
                        values.Messages.find((c) => c.Channel === "sms")
                          ?.IsOpenTicket,
                      ]}
                      onChange={(v: any) => {
                        confirmFixedBroadcast(() =>
                          setValues((prev) => ({
                            ...prev,
                            Messages: prev.Messages.map((pc) => {
                              if (pc.Channel === "sms") {
                                return {
                                  ...pc,
                                  IsOpenTicket: v[0],
                                };
                              }
                              return pc;
                            }),
                          }))
                        );
                      }}
                      options={[
                        { value: true, label: "Open ticket" },
                        { value: false, label: "Single ticket" },
                      ]}
                    />
                  </div>
                ) : (
                  <div className="my-3">
                    <label className="fw-bold">
                      Dynamic header{" "}
                      <HelpButton helpKey="broadcast-dynamic-header" />
                    </label>
                    <DynamicHeaderInput
                      placeholder="Select dynamic header"
                      value={
                        values.Messages.find((c) => c.Channel === "sms")
                          ?.DynamicHeader || ""
                      }
                      onChange={(v: any) => {
                        confirmFixedBroadcast(() =>
                          setValues((prev) => ({
                            ...prev,
                            Messages: prev.Messages.map((pc) => {
                              if (pc.Channel === "sms") {
                                return {
                                  ...pc,
                                  DynamicHeader: v,
                                };
                              }
                              return pc;
                            }),
                          }))
                        );
                      }}
                      onLoad={(v: any) => {
                        setValues((prev) => ({
                          ...prev,
                          Messages: prev.Messages.map((pc) => {
                            if (pc.Channel === "sms") {
                              return {
                                ...pc,
                                DynamicHeader: v,
                              };
                            }
                            return pc;
                          }),
                        }));
                      }}
                    />
                  </div>
                )}
              </div>
            </MyAccordionBody>
          </Accordion.Item>
        )}

        {/* {values.Messages.some((vc) => vc.Channel === "email") && (
          <Accordion.Item eventKey="email" className="bg-transparent">
            <Accordion.Header
              style={
                {
                  // "--bs-accordion-active-bg": "white",
                  // "--bs-accordion-btn-bg": "rgba(255,255,255,0.4)",
                }
              }
            >
              <span className="fw-bold">EMAIL</span>
            </Accordion.Header>
            <MyAccordionBody>
              <div>
                <div className=" my-3">
                  <label className="fw-bold">
                    Email subject <HelpButton helpKey="broadcastEmailSubject" />
                  </label>
                  <div className="">
                    <TokenEditor
                      tokenTags={["contact_field", "imported_contact_field"]}
                      rawEditorState={
                        values.Messages.find((c) => c.Channel === "email")
                          ?.EmailSubject?.EditorState
                      }
                      onChange={({
                        rawEditorState,
                        HTMLtext,
                        tokens,
                        plainText,
                        links,
                        shortlinks,
                      }: any) => {
                        setValues((prev) => ({
                          ...prev,
                          Messages: prev.Messages.map((pc) => {
                            if (pc.Channel === "email") {
                              return {
                                ...pc,
                                EmailSubject: {
                                  EditorState: rawEditorState,
                                  PlainText: plainText,
                                },
                                Tokens: [
                                  ...(pc.Tokens || []).filter(
                                    (t) => t.UsedIn !== "EmailSubject"
                                  ),
                                  ...(tokens?.map((t: any) => ({
                                    ...t,
                                    UsedIn: "EmailSubject",
                                  })) || []),
                                ],
                              };
                            }
                            return pc;
                          }),
                          BuilderConfiguration: {
                            Tokens: [
                              ...(
                                prev.BuilderConfiguration?.Tokens || []
                              ).filter(
                                (t) =>
                                  t.UsedIn !== "EmailSubject" ||
                                  t.Channel !== "email"
                              ),
                              ...(tokens?.map((t: any) => ({
                                ...t,
                                UsedIn: "EmailSubject",
                                Channel: "email",
                              })) || []),
                            ],
                          },
                        }));
                      }}
                      style={undefined}
                      label={undefined}
                      showButtons={undefined}
                      preview={undefined}
                      initialText={undefined}
                    />
                  </div>
                </div>
                <div className=" my-3">
                  <label className="fw-bold">
                    Email From <HelpButton helpKey="broadcastEmailFrom" />
                  </label>
                  <div className="">
                    <TokenEditor
                      tokenTags={["contact_field", "imported_contact_field"]}
                      rawEditorState={
                        values.Messages.find((c) => c.Channel === "email")
                          ?.EmailFrom?.EditorState
                      }
                      onChange={({
                        rawEditorState,
                        HTMLtext,
                        tokens,
                        plainText,
                        links,
                        shortlinks,
                      }: any) => {
                        setValues((prev) => ({
                          ...prev,
                          Messages: prev.Messages.map((pc) => {
                            if (pc.Channel === "email") {
                              return {
                                ...pc,
                                EmailFrom: { EditorState: rawEditorState },
                                Tokens: [
                                  ...(pc.Tokens || []).filter(
                                    (t) => t.UsedIn !== "EmailFrom"
                                  ),
                                  ...(tokens?.map((t: any) => ({
                                    ...t,
                                    UsedIn: "EmailFrom",
                                  })) || []),
                                ],
                              };
                            }
                            return pc;
                          }),
                          BuilderConfiguration: {
                            Tokens: [
                              ...(
                                prev.BuilderConfiguration?.Tokens || []
                              ).filter(
                                (t) =>
                                  t.UsedIn !== "EmailFrom" ||
                                  t.Channel !== "email"
                              ),
                              ...(tokens?.map((t: any) => ({
                                ...t,
                                UsedIn: "EmailFrom",
                                Channel: "email",
                              })) || []),
                            ],
                          },
                        }));
                      }}
                      style={undefined}
                      label={undefined}
                      showButtons={undefined}
                      preview={undefined}
                      initialText={undefined}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-between my-3">
                  <div>
                    {" "}
                    <div className="fw-bold ">
                      Overwrite Email Defaults{" "}
                      <HelpButton helpKey="broadcast-override-email-defaults" />
                    </div>
                    <div className="text-secondary">
                      Configure settings for this broadcast.
                    </div>
                  </div>
                  <Form.Check
                    type="switch"
                    id="email-defaults-switch"
                    className="fs-3 p-0"
                    checked={
                      values.Messages.find((c) => c.Channel === "email")
                        ?.EditDefaults || false
                    }
                    onChange={() =>
                      setValues((p) => ({
                        ...p,
                        Messages: p.Messages.map((c) => {
                          if (c.Channel === "email") {
                            return {
                              ...c,
                              EditDefaults: !c.EditDefaults,
                            };
                          }
                          return c;
                        }),
                      }))
                    }
                  />
                </div>
                <Collapse
                  in={
                    values.Messages.find((c) => c.Channel === "email")
                      ?.EditDefaults
                  }
                >
                  <div></div>
                </Collapse>
              </div>
            </MyAccordionBody>
          </Accordion.Item>
        )} */}

        {values.Messages.some((vc) => vc.Channel === "voice") && (
          <Accordion.Item eventKey="voice" className="bg-transparent">
            <Accordion.Header
              style={
                {
                  // "--bs-accordion-active-bg": "white",
                  // "--bs-accordion-btn-bg": "rgba(255,255,255,0.4)",
                }
              }
            >
              <span className="fw-bold">VOICE</span>
            </Accordion.Header>
            <MyAccordionBody>
              <div>
                <div className="d-flex justify-content-between my-3">
                  <label className="fw-bold ">Edit Voice Defaults</label>
                  <Form.Check
                    type="switch"
                    id="edit-defaults-switch"
                    className="fs-3 p-0"
                    checked={
                      values.Messages.find((c) => c.Channel === "voice")
                        ?.EditDefaults || false
                    }
                    onChange={() =>
                      setValues((p) => ({
                        ...p,
                        Messages: p.Messages.map((c) => {
                          if (c.Channel === "voice") {
                            return {
                              ...c,
                              EditDefaults: !c.EditDefaults,
                            };
                          }
                          return c;
                        }),
                      }))
                    }
                  />
                </div>
                <Collapse
                  in={
                    values.Messages.find((c) => c.Channel === "voice")
                      ?.EditDefaults
                  }
                >
                  <div className="">
                    <div className="mb-3">
                      <div className="fw-bold mb-2 text-nowrap">
                        Retries configuration
                      </div>
                      <div className="text-secondary mb-4">
                        Set the number of voice call retries and the delay
                        between attemps for unanswered messages. .
                      </div>

                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <div>Number of retries:</div>
                        <div className="px-2">
                          <Form.Select
                            aria-label="voice-retry-attempts"
                            value={
                              values.Messages.find((c) => c.Channel === "voice")
                                ?.RetryAttempts || ""
                            }
                            onChange={(e) =>
                              setValues((p) => ({
                                ...p,
                                Messages: p.Messages.map((c) => {
                                  if (c.Channel === "voice") {
                                    return {
                                      ...c,
                                      RetryAttempts: parseInt(e.target.value),
                                    };
                                  }
                                  return c;
                                }),
                              }))
                            }
                          >
                            <option value={1}>1</option>
                            <option value={2}>2</option>
                            <option value={3}>3</option>
                          </Form.Select>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>Delay between sending:</div>
                        <div className="d-flex justify-content-end align-items-center">
                          <div className="px-2" style={{ width: 70 }}>
                            <Form.Control
                              className="p-2"
                              aria-label="voice-retry-delay"
                              value={
                                values.Messages.find(
                                  (c) => c.Channel === "voice"
                                )?.RetryDelay || ""
                              }
                              onChange={(e) =>
                                setValues((p) => ({
                                  ...p,
                                  Messages: p.Messages.map((c) => {
                                    if (c.Channel === "voice") {
                                      return {
                                        ...c,
                                        RetryDelay: parseInt(e.target.value),
                                      };
                                    }
                                    return c;
                                  }),
                                }))
                              }
                            />
                          </div>
                          <div>
                            <Form.Select
                              aria-label="voice-retry-unit"
                              value={
                                values.Messages.find(
                                  (c) => c.Channel === "voice"
                                )?.RetryUnit || ""
                              }
                              onChange={(e) =>
                                setValues((p) => ({
                                  ...p,
                                  Messages: p.Messages.map((c) => {
                                    if (c.Channel === "voice") {
                                      return {
                                        ...c,
                                        RetryUnit: e.target.value,
                                      };
                                    }
                                    return c;
                                  }),
                                }))
                              }
                            >
                              <option value="days">Days</option>
                              <option value="hours">Hours</option>
                              <option value="minutes">Minutes</option>
                            </Form.Select>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between my-3">
                      <label className="fw-bold ">
                        Redirect to telephone number{" "}
                      </label>
                      <Form.Check
                        type="switch"
                        id="redirect-telephone-number"
                        className="fs-3 p-0"
                        checked={
                          values.Messages.find((c) => c.Channel === "voice")
                            ?.IsRedirect || false
                        }
                        onChange={() =>
                          setValues((p) => ({
                            ...p,
                            Messages: p.Messages.map((c) => {
                              if (c.Channel === "voice") {
                                return {
                                  ...c,
                                  IsRedirect: !c.IsRedirect,
                                  RedirectMessage: !c.IsRedirect
                                    ? compulsoryRedirectMessage
                                    : "",
                                };
                              }
                              return c;
                            }),
                          }))
                        }
                      />
                    </div>
                    <Collapse
                      in={
                        values.Messages.find((c) => c.Channel === "voice")
                          ?.IsRedirect
                      }
                    >
                      <div>
                        <div className="d-flex justify-content-between mb-3">
                          <label className="fw-bold ">
                            Redirect Telephone Number
                          </label>

                          <Input
                            inputType="phone"
                            value={[
                              values.Messages.find((c) => c.Channel === "voice")
                                ?.RedirectNumber || "",
                            ]}
                            onChange={(v: any) =>
                              setValues((p) => ({
                                ...p,
                                Messages: p.Messages.map((c) => {
                                  if (c.Channel === "voice") {
                                    return {
                                      ...c,
                                      RedirectNumber: v[0],
                                    };
                                  }
                                  return c;
                                }),
                              }))
                            }
                            inputHelp={undefined}
                            isMulti={undefined}
                            isDisabled={undefined}
                            isSearchable={undefined}
                            isClearable={undefined}
                            options={undefined}
                            onKeyUp={undefined}
                            onCreateOption={undefined}
                            type={undefined}
                            isOptionDisabled={undefined}
                            min={undefined}
                            max={undefined}
                            isValidNewOption={undefined}
                            loadOptions={undefined}
                            getValueLabels={undefined}
                            autoFocus={undefined}
                            label={undefined}
                            isSummary={undefined}
                            configuration={undefined}
                            placeholder={undefined}
                            maxLength={undefined}
                            isContactSet={undefined}
                            html={undefined}
                            isEmail={undefined}
                            dateTimeType={undefined}
                            maxDate={undefined}
                            components={undefined}
                          />
                        </div>
                        <div className="bg-white rounded-3 my-3 p-2">
                          <div className="fw-bold">
                            Redirect Message:{" "}
                            <HelpButton helpKey="broadcast-redirect-message" />
                          </div>
                          <div className="my-3">
                            Enter the text to be played along with the content
                            message after redirect.
                          </div>
                          <div className="">{compulsoryRedirectMessage}</div>
                          <textarea
                            className="form-control"
                            value={
                              values.Messages.find(
                                (c) => c.Channel === "voice"
                              )?.RedirectMessage?.replace(
                                compulsoryRedirectMessage,
                                ""
                              ) || ""
                            }
                            onChange={(e) =>
                              setValues((p) => ({
                                ...p,
                                Messages: p.Messages.map((c) => {
                                  if (c.Channel === "voice") {
                                    return {
                                      ...c,
                                      RedirectMessage:
                                        compulsoryRedirectMessage +
                                        e.target.value,
                                    };
                                  }
                                  return c;
                                }),
                              }))
                            }
                          />
                        </div>
                      </div>
                    </Collapse>
                  </div>
                </Collapse>
              </div>
            </MyAccordionBody>
          </Accordion.Item>
        )}

        {/* {values.Messages.some((vc) => vc.Channel === "whatsapp") && (
          <Accordion.Item eventKey="whatsapp">
            <Accordion.Header
              style={
                {
                  // "--bs-accordion-active-bg": "white",
                  // "--bs-accordion-btn-bg": "rgba(255,255,255,0.4)",
                }
              }
            >
              <span className="fw-bold">WHATSAPP</span>
            </Accordion.Header>
            <MyAccordionBody
              values={values}
              currentChannel={currentChannel}
              setValues={setValues}
              setCurrentChannel={setCurrentChannel}
            >
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
              enim ad minim veniam, quis nostrud exercitation ullamco laboris
              nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
              reprehenderit in voluptate velit esse cillum dolore eu fugiat
              nulla pariatur. Excepteur sint occaecat cupidatat non proident,
              sunt in culpa qui officia deserunt mollit anim id est laborum.
            </MyAccordionBody>
          </Accordion.Item>
        )} */}

        {/* {values.Messages.some((vc) => vc.Channel === "messenger") && (
          <Accordion.Item eventKey="messenger">
            <Accordion.Header
              style={
                {
                  // "--bs-accordion-active-bg": "white",
                  // "--bs-accordion-btn-bg": "rgba(255,255,255,0.4)",
                }
              }
            >
              <span className="fw-bold">MESSENGER</span>
            </Accordion.Header>
            <MyAccordionBody
              values={values}
              currentChannel={currentChannel}
              setValues={setValues}
              setCurrentChannel={setCurrentChannel}
            >
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
              enim ad minim veniam, quis nostrud exercitation ullamco laboris
              nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
              reprehenderit in voluptate velit esse cillum dolore eu fugiat
              nulla pariatur. Excepteur sint occaecat cupidatat non proident,
              sunt in culpa qui officia deserunt mollit anim id est laborum.
            </MyAccordionBody>
          </Accordion.Item>
        )} */}

        {/* {values.Messages.some((vc) => vc.Channel === "rcs") && (
          <Accordion.Item eventKey="rcs">
            <Accordion.Header
              style={
                {
                  // "--bs-accordion-active-bg": "white",
                  // "--bs-accordion-btn-bg": "rgba(255,255,255,0.4)",
                }
              }
            >
              <span className="fw-bold"> RCS</span>
            </Accordion.Header>
            <MyAccordionBody
              values={values}
              currentChannel={currentChannel}
              setValues={setValues}
              setCurrentChannel={setCurrentChannel}
            >
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
              enim ad minim veniam, quis nostrud exercitation ullamco laboris
              nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
              reprehenderit in voluptate velit esse cillum dolore eu fugiat
              nulla pariatur. Excepteur sint occaecat cupidatat non proident,
              sunt in culpa qui officia deserunt mollit anim id est laborum.
            </MyAccordionBody>
          </Accordion.Item>
        )} */}
      </Accordion>
    </div>
  );
}
